import isEmpty from "lodash/isEmpty";
import {
  CustomFieldsValue,
  Payee,
  PayeeEngagementType
} from "@wingspanhq/wingspan-api-sdk";
import { IPayerPayeePayerOwnedData } from "@wingspanhq/payments/dist/interfaces";
import { payeeEngagementsApi, payeesApi } from "@api";
import {
  queriesPayeeEngagements,
  queriesPayeeRows,
  queriesPayees
} from "@query";
import { selectorCounterpartyProfileWrite } from "../selectors/selectorCounterpartyProfileWrite";
import { useWSMutation, WSMutationConfig } from "../../../query/helpers";
import { WSServiceError } from "../../../utils/serviceHelper";
import { QUERY_PAYEE } from "../../../query/payee/keys";
import {
  QUERY_PAYEE_ENGAGEMENT_BY_PAYEE_ID,
  QUERY_PAYEE_ENGAGEMENTS_LIST,
  QUERY_PAYEE_ENGAGEMENTS_LIST_BY_PAYEE_ID_ALL,
  QUERY_PAYEE_ENGAGEMENTS_LIST_SIZE
} from "../../../query/payeeEngagements/keys";
import { QUERY_PAYEE_ROWS } from "../../../query/search/payee/queries/useQueryPayeeRows";
import { QUERY_PAYEE_ROWS_SUMMARY } from "../../../query/search/payee/queries/useQueryPayeeRowsSummary";
import { useXWingspanPayerId } from "../routes/RoutePayeeDetails/useXWingspanPayerId";

export type CreatePayeeInput = {
  email: string;
  name?: string;
  company?: string;
  externalId?: string;
  /**
   * Payer-owned data (custom fields + QBO integration mapping) that v3
   * `PayeeCreate` has no home for — persisted after create via the dedicated v3
   * endpoints (`updatePayeeCustomFields`, `updatePayee` QBO fields); skipped when
   * empty.
   */
  payerOwnedData?: Partial<IPayerPayeePayerOwnedData>;
  /**
   * Engagements to assign after create, via v3 `createPayeeEngagement`. Each
   * carries its `engagementType` (from the v3 `Engagement`), which the v3
   * assignment requires and v1 never sent. Assigned sequentially; omit for the
   * bulk flow, which loops engagements itself with per-item progress.
   */
  engagements?: Array<{ id: string; engagementType: PayeeEngagementType }>;
};

/**
 * Creates a contractor (payee) on v3 and issues the Wingspan invite.
 *
 * v1 `createPayee` was one fat call that created the payee, invited them, and
 * persisted payer-owned data (custom fields, QBO mapping) in a single request.
 * v3 splits these: `createPayee` takes the lean payee (email / externalId /
 * profile); the invite (implicit in v1) is an explicit `invitePayee`; custom
 * fields are persisted via the dedicated v3 `updatePayeeCustomFields`; and the
 * QBO vendor / expense-account mapping via the per-payee v3 fields on
 * `updatePayee` (`quickbooksVendorId` / `quickbooksExpenseAccountId`).
 * See V3_MIGRATION_TRACKER.md.
 *
 * Org/expansion context: v1's payer-scoped calls used the `X-Wingspan-User`
 * header; the v3 equivalent is the `xWingspanAccount` request scope (relies on
 * the D1/D24 `userId == accountId` equivalence). In the common (non-org) path
 * `useXWingspanPayerId()` is `undefined`, so no scope override is sent.
 */
export const useCreatePayee = (
  config?: WSMutationConfig<Payee, WSServiceError, CreatePayeeInput>
) => {
  const xWingspanAccount = useXWingspanPayerId();

  return useWSMutation<Payee, WSServiceError, CreatePayeeInput>(
    async input => {
      const payee = await payeesApi.createPayee({
        payeeCreate: {
          email: input.email,
          externalId: input.externalId || undefined,
          profile: selectorCounterpartyProfileWrite({
            name: input.name,
            company: input.company
          })
        },
        xWingspanAccount
      });

      await payeesApi.invitePayee({ payeeId: payee.id!, xWingspanAccount });

      // Custom fields → dedicated v3 endpoint.
      const customFields = input.payerOwnedData?.customFields;
      if (customFields && !isEmpty(customFields)) {
        await payeesApi.updatePayeeCustomFields({
          payeeId: payee.id!,
          requestBody: customFields as { [key: string]: CustomFieldsValue },
          xWingspanAccount
        });
      }

      // QBO vendor / expense-account mapping → per-payee v3 fields
      // (`PayeeUpdate.quickbooksVendorId` / `quickbooksExpenseAccountId`,
      // SDK 1.20.0 / 1.21.0). `payee.id` is the freshly-created v3 payee id.
      const quickbooks = input.payerOwnedData?.integration?.quickbooks;
      if (quickbooks && !isEmpty(quickbooks)) {
        await payeesApi.updatePayee({
          payeeId: payee.id!,
          payeeUpdate: {
            quickbooksVendorId: quickbooks.vendorId,
            quickbooksExpenseAccountId: quickbooks.expenseAccountId
          },
          xWingspanAccount
        });
      }

      for (const engagement of input.engagements ?? []) {
        await payeeEngagementsApi.createPayeeEngagement({
          payeeId: payee.id!,
          payeeEngagementCreate: {
            engagementId: engagement.id,
            engagementType: engagement.engagementType
          },
          xWingspanAccount
        });
      }

      return payee;
    },
    {
      dependencies: [
        QUERY_PAYEE_ROWS_SUMMARY,
        QUERY_PAYEE_ROWS,
        QUERY_PAYEE,
        QUERY_PAYEE_ENGAGEMENT_BY_PAYEE_ID,
        QUERY_PAYEE_ENGAGEMENTS_LIST,
        QUERY_PAYEE_ENGAGEMENTS_LIST_BY_PAYEE_ID_ALL,
        QUERY_PAYEE_ENGAGEMENTS_LIST_SIZE,
        queriesPayees.listPayees.queryKeyBase,
        queriesPayeeRows.searchPayeeRows.queryKeyBase,
        queriesPayeeEngagements.listPayeeEngagements.queryKeyBase
      ],
      ...config
    }
  );
};
